// =============================================================================
// wastageSync.js — kitchen wastage recorder (Supabase).
//
// The thin layer the kitchen "Wastage Tracking" tile calls to record a wastage
// tap into the Supabase wastage_events table (migration 0060). Sibling of
// stockSync.js / selloutSync.js.
//
// HISTORY / CONTRACT CHANGE (2026-09-12, Lane AX — wastage off Google): this
// used to be a best-effort SHADOW that ran alongside a fire-and-forget Apps
// Script `no-cors` POST which was the notional source of truth. Nothing ever
// read that Sheet copy, and the POST put the staff PIN on the wire, so the
// area was cut: the tile no longer writes to Google, and wastage_events is now
// THE record. Because of that, this function returns an HONEST result and the
// CALLER decides what the tile shows — it never claims a save that did not
// happen. It still can NEVER throw into the kitchen path (every branch returns
// an { ok, skipped?, error? } object), but a swallowed failure is no longer
// invisible: the tile reads `ok` and surfaces a retry instead of a false ✓.
//
// Inert unless dualRunEnabled() (the live kitchen host, or ?dualrun=on) —
// identical gate to sell-outs/stock, so there is exactly ONE dual-run switch
// across every module. On staging, a preview build or localhost it returns
// { ok:true, skipped:true } and writes nothing; the tile surfaces that as
// "not recorded on this preview" rather than a success.
// =============================================================================
import { supabase } from "./supabaseClient.js";
import { dualRunEnabled, siteIdForKey, shadow, ensurePass } from "./stockSync.js";
import { wastageRowsFrom } from "./wastageRows.js";

// Fresh at CALL time (the day-rollover lesson): a kiosk that slept through
// midnight must stamp the new London trading day, not a stale React value.
const londonToday = () => new Date().toLocaleDateString("en-CA", { timeZone: "Europe/London" });

// One call per wastage submit. `entries` is the array the tile built (each
// { id, ingredient, quantity, reason, ... }); we shape those into wastage_events
// rows and insert them in one round-trip.
//
// staff_id is NOT sent — the DB stamps it from the pass minted by ensurePass
// (per-action verify, which is what makes the who-did-it stamp truthful). The
// raw PIN never touches a row.
//
// Single attempt (no retry loop): there is no read flip riding on this — the
// Command Centre wastage view is review-only — and volume is tiny. Since the
// Google write was cut this IS the record, so a failed write returns ok:false
// and the TILE shows a retry rather than dropping it silently. The idem_key
// still makes a double-tap (or a manual retry) collapse to one row.
export async function recordKitchenWastage({ siteKey, staffPin, entries, day = londonToday() }) {
  try {
    if (!dualRunEnabled()) return { ok: true, skipped: true };
    const pass = await ensurePass(staffPin, siteKey);
    if (!pass?.ok) { console.warn("[wastage-sync] no login pass — write skipped"); return { ok: false, error: "no pass" }; }
    const siteId = await siteIdForKey(siteKey);
    if (!siteId) return { ok: false, error: "no site id for " + siteKey };
    const rows = wastageRowsFrom({ siteId, siteKey, entries, day });
    if (!rows.length) return { ok: true, skipped: true };
    // ON CONFLICT (idem_key) DO NOTHING: a retried submit re-sends the same entry
    // ids -> same idem_keys -> the unique index collapses them to one row each.
    return shadow("wastage submit", () =>
      supabase.from("wastage_events").upsert(rows, { onConflict: "idem_key", ignoreDuplicates: true }));
  } catch (e) {
    console.warn("[wastage-sync] recordKitchenWastage threw:", e?.message || e);
    return { ok: false, error: e };
  }
}
